import React, { useState, useEffect } from 'react';
import { useCep } from '../../hooks/useCep';

const CepField = ({ 
  value, 
  onChange, 
  onAddressChange, 
  className = '', 
  placeholder = 'CEP',
  disabled = false,
  autoComplete = 'postal-code'
}) => {
  const { searchCep, loading, error, addressData, formatCep, isValidCep } = useCep();
  const [displayValue, setDisplayValue] = useState('');

  // Sincronizar valor inicial
  useEffect(() => {
    if (value) {
      setDisplayValue(formatCep(value));
    }
  }, [value, formatCep]);

  // Quando os dados do endereço mudarem, notificar o componente pai
  useEffect(() => {
    if (addressData && onAddressChange) {
      onAddressChange(addressData);
    }
  }, [addressData]); // Removido onAddressChange da dependência

  const handleChange = (e) => {
    const rawValue = e.target.value;
    const cleanValue = rawValue.replace(/[^\d]/g, '');
    
    // Limitar a 8 dígitos
    if (cleanValue.length <= 8) {
      const formattedValue = formatCep(cleanValue);
      setDisplayValue(formattedValue);
      
      // Chamar onChange com valor limpo
      if (onChange) {
        onChange(cleanValue);
      }
      
      // Se CEP está completo e válido, buscar automaticamente
      if (cleanValue.length === 8 && isValidCep(cleanValue)) {
        searchCep(cleanValue);
      }
    }
  };

  const handleBlur = () => {
    // Buscar CEP no blur se ainda não foi buscado
    const cleanValue = displayValue.replace(/[^\d]/g, '');
    if (cleanValue.length === 8 && isValidCep(cleanValue) && !addressData) {
      searchCep(cleanValue);
    }
  };

  return (
    <div className="w-full">
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`
            w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
            ${loading ? 'pr-10' : ''}
            ${error ? 'border-red-500 focus:ring-red-500' : ''}
            ${addressData ? 'border-green-500' : ''}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
            ${className}
          `}
        />
        
        {/* Indicador de carregamento */}
        {loading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {/* Ícone de sucesso */}
        {addressData && !loading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>
      
      {/* Mensagem de erro */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      
      {/* Informações do endereço encontrado */}
      {addressData && !error && (
        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-800">
            {addressData.endereco && `${addressData.endereco}, `}
            {addressData.bairro && `${addressData.bairro}, `}
            {addressData.cidade && `${addressData.cidade} - `}
            {addressData.estado}
          </p>
        </div>
      )}
    </div>
  );
};

export default CepField;
